import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE from '../config';
import {
  Banner, Loading, Empty, StatusBadge, FlowBadge, SectionIntro, Modal,
  formatDateTime, errorMessage,
} from '../components/ui';
import './Applications.css';

const STATUSES = ['Pending Review', 'Reviewed', 'Ready to Send', 'Sent', 'Send Failed'];
const FLOWS = ['New Issue', 'Renewal', 'Denied'];

const CERT_STATE = {
  Valid: { cls: 'badge-green', label: 'Valid' },
  Expiring: { cls: 'badge-amber', label: 'Expiring' },
  Expired: { cls: 'badge-red', label: 'Expired' },
  Unknown: { cls: 'badge-grey', label: 'No dates' },
};

/**
 * The live certificate behind an application, and how long it has left.
 *
 * Separate from Status on purpose: Status is where the application is in the
 * workflow, this is whether the facility currently holds a valid certificate.
 * The two move independently — a renewal being worked sits at "Reviewed" while
 * the certificate it replaces is already expired.
 */
function CertificateCell({ cert }) {
  if (!cert) return <span className="apps-missing">Never issued</span>;

  const state = CERT_STATE[cert.state] || CERT_STATE.Unknown;
  const days = cert.DaysRemaining;

  return (
    <span className="apps-cert">
      <span className={`badge ${state.cls}`}>{state.label}</span>
      {days !== null && days !== undefined && (
        <span className={`apps-cert-days${cert.state === 'Expired' ? ' is-red' : cert.state === 'Expiring' ? ' is-amber' : ''}`}>
          {days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`}
        </span>
      )}
    </span>
  );
}

function Applications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deleting is irreversible, so it is Super Admin work.
  let currentUser = {};
  try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { currentUser = {}; }
  const isSuperAdmin = currentUser.role === 'Super Admin';

  const [rows, setRows] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);

  const [search, setSearch] = useState('');
  const status = searchParams.get('status') || '';
  const flowType = searchParams.get('flowType') || '';
  const inspectorFilter = searchParams.get('inspector') || '';
  const certFilter = searchParams.get('certificate') || '';

  // Allocation state: `allocating` is the application(s) being edited.
  const [allocating, setAllocating] = useState(null); // null | { rows: [...] }
  const [draftIds, setDraftIds] = useState([]);
  const [savingAlloc, setSavingAlloc] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Deletion state: `deleting` is the row(s) awaiting confirmation.
  const [deleting, setDeleting] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [busyDelete, setBusyDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (status) params.status = status;
      if (flowType) params.flowType = flowType;
      const [appsRes, inspRes] = await Promise.all([
        axios.get(`${API_BASE}/api/applications`, { params }),
        axios.get(`${API_BASE}/api/inspectors`),
      ]);
      setRows(appsRes.data);
      setInspectors(inspRes.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load applications.'));
    } finally {
      setLoading(false);
    }
  }, [status, flowType]);

  useEffect(() => { load(); }, [load]);

  // Free-text search and the inspector filter run client-side so typing stays instant.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (inspectorFilter) {
        const has = (r.inspectors || []).some((i) => String(i.Id) === inspectorFilter);
        if (inspectorFilter === 'none' ? (r.inspectors || []).length > 0 : !has) return false;
      }
      if (certFilter) {
        const state = r.certificate?.state || 'none';
        if (certFilter === 'due') {
          // What a manager actually wants to see: anything needing a renewal now.
          if (state !== 'Expired' && state !== 'Expiring') return false;
        } else if (state !== certFilter) return false;
      }
      if (!q) return true;
      return [r.CompanyName, r.NameThatWillBeUsed, r.ApplicantName, r.ApplicantEmail,
        r.InternalAccountCode, r.CertificateTypesRaw,
        ...(r.inspectors || []).flatMap((i) => [i.Name, i.Email])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, inspectorFilter, certFilter]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchParams(new URLSearchParams());
  };

  const sync = async () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_BASE}/api/applications/sync`);
      const { imported, warnings } = res.data;
      setSuccess(
        imported === 0
          ? 'No new applications on the sheet.'
          : `Imported ${imported} new application${imported === 1 ? '' : 's'}.`,
      );
      if (warnings?.length) setError(warnings.join(' '));
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not read the Google Sheet.'));
    } finally {
      setSyncing(false);
    }
  };

  // ---------------------------------------------------------- allocation

  const openAllocate = (targetRows) => {
    // Pre-tick only what every selected application already shares, so a bulk
    // save cannot silently strip an allocation the others did not have.
    const common = targetRows.length === 1
      ? (targetRows[0].inspectors || []).map((i) => i.Id)
      : targetRows.reduce((acc, r, idx) => {
        const ids = (r.inspectors || []).map((i) => i.Id);
        return idx === 0 ? ids : acc.filter((id) => ids.includes(id));
      }, []);
    setDraftIds(common);
    setAllocating({ rows: targetRows });
  };

  const saveAllocation = async () => {
    setSavingAlloc(true);
    setError('');
    try {
      for (const row of allocating.rows) {
        await axios.put(`${API_BASE}/api/applications/${row.Id}/inspectors`, { inspectorIds: draftIds });
      }
      const n = allocating.rows.length;
      setSuccess(
        n === 1
          ? `Inspector allocation saved for ${allocating.rows[0].CompanyName || 'the application'}.`
          : `Inspector allocation saved for ${n} applications.`,
      );
      setAllocating(null);
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSavingAlloc(false);
    }
  };

  // ------------------------------------------------------------- deletion

  const confirmDelete = async () => {
    setBusyDelete(true);
    setError('');
    const done = [];
    const refused = [];

    // One at a time so a record the server refuses — one already sent to a
    // client — stops only itself, and the manager is told which and why.
    for (const row of deleting) {
      try {
        await axios.delete(`${API_BASE}/api/applications/${row.Id}`, { data: { reason: deleteReason } });
        done.push(row);
      } catch (err) {
        refused.push({ row, why: errorMessage(err) });
      }
    }

    setBusyDelete(false);
    setDeleting(null);
    setDeleteReason('');
    setSelected(new Set());

    if (done.length) {
      setSuccess(done.length === 1
        ? `Deleted ${done[0].CompanyName || 'the application'}.`
        : `Deleted ${done.length} applications.`);
    }
    if (refused.length) {
      setError(refused.map((r) => `${r.row.CompanyName || `#${r.row.Id}`}: ${r.why}`).join(' '));
    }
    await load();
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.Id));
  const toggleSelectAll = () => {
    setSelected(allVisibleSelected ? new Set() : new Set(filtered.map((r) => r.Id)));
  };

  const hasFilters = !!(status || flowType || search || inspectorFilter || certFilter);
  const selectedRows = filtered.filter((r) => selected.has(r.Id));

  // Handed to the detail page so its back button returns to this list with the
  // status and flow filters still applied.
  const qs = searchParams.toString();
  const backHere = { to: `/applications${qs ? `?${qs}` : ''}`, label: 'All Applications' };

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>All Applications</h2>
            <p className="page-subtitle">
              Every Certificate of Good Standing application pulled from the Google Form. Allocate
              an inspector here, then open one to record the compliance decision and send the documents.
            </p>
          </div>
          <div className="page-header-actions">
            <button className="btn btn-secondary" onClick={sync} disabled={syncing}>
              {syncing ? 'Checking sheet…' : 'Sync from sheet'}
            </button>
            <button className="btn btn-secondary" onClick={load} disabled={loading}>Refresh</button>
          </div>
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <div className="filter-row">
          <input
            className="filter-search"
            type="text"
            placeholder="Search company, applicant, email, account code or inspector…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search applications"
          />

          <select value={status} onChange={(e) => setFilter('status', e.target.value)} aria-label="Filter by status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <select value={flowType} onChange={(e) => setFilter('flowType', e.target.value)} aria-label="Filter by outcome">
            <option value="">All outcomes</option>
            {FLOWS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>

          <select
            value={inspectorFilter}
            onChange={(e) => setFilter('inspector', e.target.value)}
            aria-label="Filter by inspector"
          >
            <option value="">All inspectors</option>
            <option value="none">— not allocated —</option>
            {inspectors.map((i) => (
              <option key={i.Id} value={String(i.Id)}>{i.Name || i.Email}</option>
            ))}
          </select>

          <select
            value={certFilter}
            onChange={(e) => setFilter('certificate', e.target.value)}
            aria-label="Filter by certificate validity"
          >
            <option value="">Any certificate</option>
            <option value="due">Renewal due (expiring or expired)</option>
            <option value="Expired">Expired</option>
            <option value="Expiring">Expiring soon</option>
            <option value="Valid">Valid</option>
            <option value="none">— never issued —</option>
          </select>

          {hasFilters && (
            <button className="btn btn-ghost btn-sm filter-clear" onClick={clearFilters}>Clear filters</button>
          )}
        </div>

        {selected.size > 0 && (
          <div className="apps-bulkbar">
            <span>
              <strong>{selected.size}</strong> selected
            </span>
            <button className="btn btn-primary btn-sm" onClick={() => openAllocate(selectedRows)}>
              Allocate inspector to {selected.size}
            </button>
            {isSuperAdmin && (
              <button className="btn btn-danger btn-sm"
                onClick={() => { setDeleting(selectedRows); setDeleteReason(''); }}>
                Delete {selected.size === 1 ? 'this one' : `these ${selected.size}`}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}

        {loading ? (
          <Loading label="Loading applications…" />
        ) : filtered.length === 0 ? (
          <Empty
            title={hasFilters ? 'No applications match these filters.' : 'No applications yet.'}
            hint={
              hasFilters
                ? 'Clear the filters to see everything.'
                : 'New Google Form entries appear here automatically, or use "Sync from sheet".'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="data-table apps-table">
              <thead>
                <tr>
                  <th className="apps-check">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all visible applications"
                    />
                  </th>
                  <th className="nowrap">Received</th>
                  <th>Applicant</th>
                  <th>Applied for</th>
                  <th className="nowrap">Account code</th>
                  <th className="nowrap">Inspector</th>
                  <th className="nowrap">Status</th>
                  <th className="nowrap">Certificate</th>
                  <th className="nowrap">Outcome</th>
                  <th className="nowrap">Docs</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.Id}
                    className={`row-clickable${selected.has(row.Id) ? ' apps-selected' : ''}`}
                    onClick={() => navigate(`/applications/${row.Id}`, { state: { from: backHere } })}
                  >
                    <td className="apps-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.Id)}
                        onChange={() => toggleSelected(row.Id)}
                        aria-label={`Select ${row.CompanyName || 'application'}`}
                      />
                    </td>
                    <td className="nowrap">{formatDateTime(row.CreatedAt)}</td>
                    <td>
                      <span className="apps-company">{row.CompanyName || 'Unnamed applicant'}</span>
                      <span className="apps-sub">{row.ApplicantEmail || 'No email on file'}</span>
                    </td>
                    <td className="apps-types">{row.CertificateTypesRaw || '—'}</td>
                    <td className="nowrap">
                      {row.InternalAccountCode || <span className="apps-missing">Not captured</span>}
                    </td>
                    <td className="nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`apps-inspector${(row.inspectors || []).length ? '' : ' is-empty'}`}
                        onClick={() => openAllocate([row])}
                        title="Allocate an inspector"
                      >
                        {(row.inspectors || []).length === 0
                          ? 'Allocate'
                          : row.inspectors.map((i) => i.Name || i.Email).join(', ')}
                      </button>
                    </td>
                    <td className="nowrap"><StatusBadge status={row.Status} /></td>
                    <td className="nowrap"><CertificateCell cert={row.certificate} /></td>
                    <td className="nowrap"><FlowBadge flowType={row.FlowType} /></td>
                    <td className="numeric nowrap">{row.DocumentCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <SectionIntro>
          {filtered.length} of {rows.length} application{rows.length === 1 ? '' : 's'} shown.
          The allocated inspector is CC'd on the client's outcome email, approval or rejection.
        </SectionIntro>
      </div>

      {allocating && (
        <Modal
          title={
            allocating.rows.length === 1
              ? `Inspector for ${allocating.rows[0].CompanyName || 'this application'}`
              : `Inspector for ${allocating.rows.length} applications`
          }
          subtitle="Everyone ticked is copied on the outcome email — approval and rejection alike."
          onClose={() => setAllocating(null)}
        >
          {allocating.rows.length > 1 && (
            <div className="callout callout-warning">
              <span className="callout-label">Applies to all {allocating.rows.length}</span>
              Saving replaces the inspector allocation on every selected application. Only
              inspectors already common to all of them are ticked.
            </div>
          )}

          {inspectors.length === 0 ? (
            <p className="apps-none">
              No inspectors configured yet. Add them on the Inspectors page first.
            </p>
          ) : (
            <div className="apps-inspector-list">
              {inspectors.map((i) => (
                <label className="checkbox-row" key={i.Id}>
                  <input
                    type="checkbox"
                    checked={draftIds.includes(i.Id)}
                    onChange={() => setDraftIds((prev) =>
                      prev.includes(i.Id) ? prev.filter((x) => x !== i.Id) : [...prev, i.Id])}
                  />
                  <span>
                    <strong>{i.Name || i.Email}</strong>
                    {i.Name && <span className="apps-inspector-email">{i.Email}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setAllocating(null)}>Cancel</button>
            {draftIds.length > 0 && (
              <button className="btn btn-ghost" onClick={() => setDraftIds([])}>Clear all</button>
            )}
            <button
              className="btn btn-primary"
              onClick={saveAllocation}
              disabled={savingAlloc || inspectors.length === 0}
            >
              {savingAlloc ? 'Saving…' : 'Save allocation'}
            </button>
          </div>
        </Modal>
      )}

      {deleting && (
        <Modal
          title={deleting.length === 1
            ? `Delete ${deleting[0].CompanyName || 'this application'}?`
            : `Delete ${deleting.length} applications?`}
          subtitle="For duplicate submissions. This cannot be undone."
          onClose={() => setDeleting(null)}
        >
          <div className="callout callout-warning">
            <span className="callout-label">What goes</span>
            The application, its commodity and inspector allocations, and any documents
            already produced for it — files included. The activity log entry survives, and
            the form response is marked as dismissed so the next sheet sync does not pull
            it back in.
          </div>

          <div className="apps-delete-list">
            {deleting.map((r) => (
              <div className="apps-delete-row" key={r.Id}>
                <span>
                  <strong>{r.CompanyName || 'Unnamed applicant'}</strong>
                  <span className="apps-inspector-email">{r.ApplicantEmail || 'no email'}</span>
                </span>
                <StatusBadge status={r.Status} />
              </div>
            ))}
          </div>

          {deleting.some((r) => r.Status === 'Sent') && (
            <div className="callout callout-note">
              <span className="callout-label">Some will be refused</span>
              An application already emailed to the client cannot be deleted — the record is
              the only evidence of what they were sent. Those will be reported and left alone.
            </div>
          )}

          <div className="field">
            <label className="field-required" htmlFor="delete-reason">Reason</label>
            <textarea id="delete-reason" rows={2} value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g. Duplicate of the submission received the same day." />
            <span className="field-hint">Kept in the activity log after the record is gone.</span>
          </div>

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={confirmDelete}
              disabled={!deleteReason.trim() || busyDelete}>
              {busyDelete ? 'Deleting…' : `Delete ${deleting.length === 1 ? 'it' : `all ${deleting.length}`}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Applications;
